import { useCallback } from 'react';

import { getApiErrorDetail } from '@klareospace/pharos-lib-front-utils';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { orderKeys } from '../api';
import { resolveOrderUpdateErrorMessage, shouldRefetchOnOrderUpdateError } from '../utils/order-update-error';

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Returns a function that maps an order-update rejection to a user-facing message, and — for
 * `Conflict.OrderInExecution` / `Conflict.OrderTerminal` — refetches the order so the drawer's
 * editability locks refresh immediately (the field the user tried to edit may have just become
 * non-editable because a task started executing or the order reached a terminal status).
 */
export function useOrderUpdateErrorMessage(orderId: string): (error: unknown) => string {
  const { t } = useTranslation('orders');
  const queryClient = useQueryClient();

  return useCallback(
    (error: unknown): string => {
      const detail = getApiErrorDetail(error);
      if (shouldRefetchOnOrderUpdateError(detail.errorCode)) {
        void queryClient.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
      }
      return resolveOrderUpdateErrorMessage(t, detail);
    },
    [orderId, queryClient, t],
  );
}
